import { EyeIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  formatDate,
  formatDateWithTime,
} from '../../../../common-utils/formatters';
import type { BlogPost } from '../../../@types/BlogPost';
import { BlogPostStatusEnum } from '../../../@types/BlogPost';
import type { RootState } from '../../../store/store';
import classNames from '../../../utils/classNames';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import tailwindConfig from '../../../utils/tailwindColors';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';

export default function BlogPostList() {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const headers = [
    { title: 'Titre' },
    { title: 'Statut' },
    { title: 'Créé le' },
    { title: 'Dernière modification' },
    { title: 'Actions' },
  ];

  const fetchBlogPosts = async() => {
    if (!jwt) {
      return;
    }
    try {
      const response = await fetch(
        '/api/blog-posts?limit=1000',
        JWTHeader(jwt),
      );
      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }
      const data = await response.json();
      setBlogPosts(data.results || []);
    } catch (error) {
      console.error('Failed to fetch blog posts :', error);
      showToast(
        'error',
        'Une erreur est survenue lors du chargement des articles.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusToggle = async(blogPost: BlogPost) => {
    if (!jwt || updatingStatus !== null) {
      return;
    }

    const newStatus =
      blogPost.status === BlogPostStatusEnum.DRAFT
        ? BlogPostStatusEnum.PUBLIC
        : BlogPostStatusEnum.DRAFT;

    setUpdatingStatus(blogPost.id);
    try {
      const response = await fetch(`/api/blog-posts/${blogPost.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: JWTAuthorization(jwt),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Failed to update blog post status');
      }

      // Optimistic update
      setBlogPosts((prevPosts) =>
        prevPosts.map((post) =>
          post.id === blogPost.id ? { ...post, status: newStatus } : post,
        ),
      );

      showToast('success', 'Statut mis à jour avec succès.');

      // Refresh the list after a short delay to ensure server-side changes are reflected
      setTimeout(() => {
        fetchBlogPosts();
      }, 500);
    } catch (error) {
      console.error('Failed to update blog post status :', error);
      showToast(
        'error',
        'Une erreur est survenue lors de la mise à jour du statut.',
      );
      // Revert optimistic update on error
      await fetchBlogPosts();
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleViewBlogPost = (blogPost: BlogPost) => {
    window.open(
      ROUTES.EXTERNAL_BLOG_POST.replace(':slug', blogPost.slug),
      '_blank',
    );
  };

  const formattedData = blogPosts.map((post) => ({
    id: post.id,
    Titre: post.title,
    Statut: (
      <div className="flex justify-center">
        <button
          onClick={() => handleStatusToggle(post)}
          disabled={updatingStatus !== null}
          className={`
            flex items-center space-x-1 rounded-full px-3 
            py-1 text-sm font-medium
            transition-all duration-200 ease-in-out
            ${updatingStatus === post.id ? 'opacity-75' : ''}
            ${
              post.status === BlogPostStatusEnum.DRAFT
                ? 'bg-yellow-full text-yellow-800 hover:bg-yellow-200'
                : 'bg-green-100 text-green-800 hover:bg-green-200'
            }
            ${updatingStatus !== null && updatingStatus !== post.id ? 'cursor-not-allowed opacity-50' : ''}
          `}
        >
          {updatingStatus === post.id ? (
            <>
              <Spinner size="sm" className="mr-2" />
              <span>Mise à jour...</span>
            </>
          ) : post.status === BlogPostStatusEnum.DRAFT ? (
            'Brouillon'
          ) : (
            'Public'
          )}
        </button>
      </div>
    ),
    'Créé le': formatDate(post.createdAt),
    'Dernière modification': formatDateWithTime(post.updatedAt),
    Actions: (
      <div className="flex flex-col justify-center gap-2">
        <button
          onClick={() =>
            navigate(
              ROUTES.ADMIN_BLOG_POST_EDIT.replace(':id', post.id.toString()),
            )
          }
          className="flex items-center space-x-1 rounded-xl bg-rgpd-blue-dark px-3 py-1.5 text-white transition-colors hover:bg-rgpd-blue-darker"
        >
          <PencilIcon className="h-4 w-4 shrink-0" />
          <span className="shrink-0 grow text-center">Modifier</span>
        </button>
        <button
          onClick={() => handleViewBlogPost(post)}
          disabled={post.status !== BlogPostStatusEnum.PUBLIC}
          className={classNames(
            'bg-rgpd-blue-dark text-white px-3 py-1.5 rounded-xl flex items-center space-x-1 transition-colors',
            post.status === BlogPostStatusEnum.PUBLIC
              ? 'hover:bg-rgpd-blue-darker'
              : 'opacity-50 cursor-not-allowed',
          )}
        >
          <EyeIcon className="h-4 w-4 shrink-0" />
          <span className="shrink-0 grow text-center">Voir</span>
        </button>
      </div>
    ),
  }));

  useEffect(() => {
    fetchBlogPosts();
  }, [jwt]);

  if (isLoading) {
    return (
      <div className="mx-auto p-4">
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div>
            <h1 className="text-2xl font-bold">Articles de blog</h1>
            <p className="mt-2">
              Gérez ici vos articles de blog. Vous pouvez créer, modifier et
              publier des articles qui apparaîtront sur votre site. Les articles
              en brouillon ne sont visibles que par les administrateurs.
            </p>
          </div>

          <div className="mt-8 mb-4 flex justify-center">
            <Skeleton width={170} height={40} className="rounded-lg" />
          </div>

          <MUITable
            data={[]}
            headers={headers}
            isSearchable
            sortableColumns={['Titre', 'Créé le']}
          />
          <Skeleton
            count={5}
            height={45}
            className="mt-2"
            highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto p-4">
      <div className="rounded-lg bg-white p-6 shadow-md">
        <div>
          <h1 className="text-2xl font-bold">Articles de blog</h1>
          <p className="mt-2">
            Gérez ici vos articles de blog. Vous pouvez créer, modifier et
            publier des articles qui apparaîtront sur votre site. Les articles
            en brouillon ne sont visibles que par les administrateurs.
          </p>
        </div>
        <div className="mt-8 mb-4 flex justify-center">
          <button
            onClick={() => navigate(`${ROUTES.ADMIN_BLOG_POSTS}/new`)}
            className="flex items-center rounded-lg bg-rgpd-blue-dark px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-darker"
          >
            <PlusIcon className="mr-2 h-5 w-5" />
            Nouvel article
          </button>
        </div>

        <MUITable
          data={formattedData}
          headers={headers}
          isSearchable
          sortableColumns={['Titre', 'Créé le']}
        />
        {blogPosts.length === 0 && (
          <div className="mt-4 flex justify-center">
            <Badge mode="warning" text="Aucun article trouvé" />
          </div>
        )}
      </div>
    </div>
  );
}
